/**
 * GoogleButton — "Continue with Google" for the Login / Register pages.
 *
 * Uses Google Identity Services (GIS): Google's own script renders the
 * button, runs the whole handshake, and hands back a signed ID token.
 * We forward that token to POST /api/auth/google, which verifies it
 * server-side and sets the same `tile_session` cookie the password flow
 * sets. No client secret, no redirect URI, no popup handling of our own.
 *
 * Config comes from GET /api/auth/google/config rather than a build-time
 * env var, so one tile-auth setting drives every frontend origin and the
 * bundle doesn't need rebuilding when the client ID changes. Until a real
 * client ID is configured the endpoint reports `enabled: false` and this
 * component renders NOTHING — the password form is untouched.
 */
import React, { useEffect, useRef, useState } from 'react';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

type Props = {
  /** Where to land after a successful sign-in. */
  redirectTo: string;
  /** Surfaced through the page's existing error banner. */
  onError: (message: string) => void;
  /** Button caption — "signin_with" on Login, "signup_with" on Register. */
  text?: 'signin_with' | 'signup_with';
};

/**
 * Load the GIS script once per page, even if two instances mount. The
 * promise is module-level so a second caller awaits the first load
 * instead of injecting a duplicate <script>.
 */
let gisPromise: Promise<void> | null = null;
function loadGis(): Promise<void> {
  if (gisPromise) return gisPromise;

  gisPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    if (existing) {
      // Injected by an earlier mount that has since unmounted.
      if ((window as any).google?.accounts?.id) resolve();
      else existing.addEventListener('load', () => resolve(), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load Google sign-in.'));
    document.head.appendChild(script);
  });

  return gisPromise;
}

export default function GoogleButton({ redirectTo, onError, text = 'signin_with' }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  // Keep the latest callbacks reachable from the GIS callback, which is
  // registered once and would otherwise close over the first render's
  // props.
  const redirectRef = useRef(redirectTo);
  const onErrorRef = useRef(onError);
  redirectRef.current = redirectTo;
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let clientId = '';
      try {
        const res = await fetch('/api/auth/google/config', { credentials: 'same-origin' });
        if (!res.ok) return; // Not configured or unreachable — stay hidden.
        const cfg = (await res.json()) as { enabled?: boolean; clientId?: string };
        if (!cfg.enabled || !cfg.clientId) return;
        clientId = cfg.clientId;
      } catch {
        return; // Never block password login on this probe.
      }

      try {
        await loadGis();
      } catch {
        return; // Script blocked (offline, extension) — stay hidden.
      }

      if (cancelled || !hostRef.current) return;
      const google = (window as any).google;
      if (!google?.accounts?.id) return;

      google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response: { credential?: string }) => {
          if (!response.credential) {
            onErrorRef.current('Google did not return a credential.');
            return;
          }
          setBusy(true);
          try {
            const res = await fetch('/api/auth/google', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ credential: response.credential }),
            });
            const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
            if (!res.ok) {
              onErrorRef.current((data.error as string) || 'Google sign-in failed.');
              return;
            }
            // Server set the cookie — same landing as the password flow.
            window.location.assign(redirectRef.current);
          } catch (err) {
            onErrorRef.current(err instanceof Error ? err.message : 'Network error');
          } finally {
            setBusy(false);
          }
        },
      });

      google.accounts.id.renderButton(hostRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text,
        shape: 'rectangular',
        logo_alignment: 'center',
        // GIS renders into an iframe at a fixed pixel width, so it can't
        // be made fluid with CSS. 336 = the 400px card minus its 32px
        // padding each side.
        width: 336,
      });

      if (!cancelled) setEnabled(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [text]);

  return (
    <div className={enabled ? 'auth-google' : undefined} aria-busy={busy || undefined}>
      <div ref={hostRef} className="auth-google-host" />
      {enabled ? (
        <div className="auth-divider">
          <span>or</span>
        </div>
      ) : null}
    </div>
  );
}
